import React, { useState, useMemo, useEffect } from 'react';
import styles from './QuestionsNavigator.module.css';
import type { question, test } from '@/features/simulationConfiguration/types.api.ts';

interface QuestionsNavigatorProps {
    questions: question[];
    tests: test[];
    answeredQuestions: Record<string, string>;
    currentQuestionIndex: number;
    onQuestionSelect: (index: number) => void;
    questionsPerSection?: number;
}

const QuestionsNavigator: React.FC<QuestionsNavigatorProps> = ({
    questions,
    tests,
    answeredQuestions,
    currentQuestionIndex,
    onQuestionSelect,
}) => {
    const totalQuestions = questions.length;

    const sections = useMemo(() => {
        if (!tests || tests.length === 0 || questions.length === 0) return [];

        const sessionGlobalStart = tests[0].start_number || 1;

        let questionCounter = 0;

        return tests.map((test, testIndex) => {
            const relativeStart = (test.start_number || 0) - sessionGlobalStart + 1;
            const relativeEnd = (test.end_number || 0) - sessionGlobalStart + 1;

            const numQuestionsInTest = questions.filter(q => {
                const qNum = q.display_number;
                return qNum && qNum >= relativeStart && qNum <= relativeEnd;
            }).length;

            const questionIndices = Array.from({ length: numQuestionsInTest }, (_, i) => questionCounter + i);

            const startDisplayNumber = questionCounter + 1;
            questionCounter += numQuestionsInTest;
            const endDisplayNumber = questionCounter;

            return {
                index: testIndex,
                testName: test.test_type.name,
                startDisplayNumber: startDisplayNumber,
                endDisplayNumber: endDisplayNumber,
                questionIndices: questionIndices,
            };
        });
    }, [tests, questions]);

    const currentSection = useMemo(() => {
        if (!sections) return undefined;
        return sections.find(section => section.questionIndices.includes(currentQuestionIndex));
    }, [currentQuestionIndex, sections]);

    const [expandedSections, setExpandedSections] = useState<Set<number>>(new Set());

    useEffect(() => {
        if (currentSection && !expandedSections.has(currentSection.index)) {
            setExpandedSections(prev => new Set(prev).add(currentSection.index));
        }
    }, [currentSection, expandedSections]);

    const toggleSection = (sectionIndex: number) => {
        setExpandedSections(prev => {
            const newSet = new Set(prev);
            if (newSet.has(sectionIndex)) {
                newSet.delete(sectionIndex);
            } else {
                newSet.add(sectionIndex);
            }
            return newSet;
        });
    };

    return (
        <div className={styles.navigatorContainer}>
            <div className={styles.summary}>
                <div className={styles.summaryItem}>
                    <span className={styles.summaryLabel}>Total:</span>
                    <span className={styles.summaryValue}>{totalQuestions}</span>
                </div>
                <div className={styles.summaryItem}>
                    <span className={styles.summaryLabel}>Respondidas:</span>
                    <span className={styles.summaryValue}>{Object.keys(answeredQuestions).length}</span>
                </div>
                <div className={styles.summaryItem}>
                    <span className={styles.summaryLabel}>Actual:</span>
                    <span className={`${styles.summaryValue} ${styles.highlight}`}>{currentQuestionIndex + 1}</span>
                </div>
            </div>

            <div className={styles.sections}>
                {sections.map((section) => {
                    const isExpanded = expandedSections.has(section.index);
                    const isCurrent = section.index === currentSection?.index;

                    return (
                        <div key={section.index} className={`${styles.section} ${isCurrent ? styles.currentSection : ''}`}>
                            <button className={styles.sectionHeader} onClick={() => toggleSection(section.index)}>
                                <div className={styles.sectionInfo}>
                                    <span className={styles.sectionTitle}>{section.testName}</span>
                                    <span className={styles.questionRange}>
                                        (Preguntas {section.startDisplayNumber}-{section.endDisplayNumber})
                                    </span>
                                </div>
                                <svg className={`${styles.chevron} ${isExpanded ? styles.expanded : ''}`} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <path strokeLinecap="round" strokeLinejoin="round" d="m19 9-7 7-7-7" />
                                </svg>
                            </button>

                            {isExpanded && (
                                <div className={styles.questionsGrid}>
                                    {section.questionIndices.map((questionIndex) => {
                                        const questionObj = questions[questionIndex];
                                        if (!questionObj) return null;

                                        const isCurrent = questionIndex === currentQuestionIndex;
                                        const isAnswered = Object.prototype.hasOwnProperty.call(answeredQuestions, String(questionObj.id));

                                        const displayQuestionNumber = questionIndex + 1;

                                        return (
                                            <button
                                                key={questionIndex}
                                                className={`${styles.questionButton} ${isCurrent ? styles.current : ''} ${isAnswered ? styles.answered : ''}`}
                                                onClick={() => onQuestionSelect(questionIndex)}
                                                aria-label={`Ir a la pregunta ${displayQuestionNumber}`}
                                            >
                                                {displayQuestionNumber}
                                            </button>
                                        );
                                    })}
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

export default QuestionsNavigator;